import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, Segmented, Upload, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import { ExtendedMessage } from '@app/components';
import { MB_RATIO } from '@app/components/DraggerComponent/data/constants';
import {
  extensions,
  MAX_FILES_SIZE_SENDOUT,
  MAX_FILES_SIZE_SENDOUT_MB,
} from '@app/components/MessageInputWithFilesUpload/data';
import {
  MESSAGE_MODE_AI,
  MESSAGE_MODE_FIXED,
} from '@app/components/MessageInputWithFilesUpload/data/messageMode';
import type { Main } from '@app/components/MessageInputWithFilesUpload/MessageInputWithFilesUpload.types';
import {
  AI_PROMPT_MAX_LENGTH,
  FRONT_WARNING,
  INSTAGRAM,
  INSTAGRAM_MAX_MESSAGE_LENGTH,
  MAX_FIELD_LENGTH_5000,
  LOYALTY_PROGRAM_SENDOUTS,
  PREFIX,
} from '@app/constants';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import type { UploadFile } from 'antd';
import type { FC } from 'react';

import '@app/components/MessageInputWithFilesUpload/styles/MessageInputWithFilesUpload.desktop.scss';

const { Text } = Typography;

const MESSAGE_MODE_OPTIONS = [
  { label: 'Фиксированное сообщение', value: MESSAGE_MODE_FIXED },
  { label: 'Сгенерированное ИИ', value: MESSAGE_MODE_AI },
];

export const MessageInputWithFilesUpload: FC<Main> = ({
  formInstance,
  onDisabledChange,
  selectedClientIds = [],
  socialType = null,
  messageFieldFormItemName = 'message',
  onChange,
  showFiles = true,
  showAiToggle = false,
  messageModeFormItemName = 'messageMode',
  aiPromptFormItemName = 'aiPrompt',
}) => {
  const [filesList, setFilesList] = useState<UploadFile[]>([]);

  const { showNotification } = useNotification();

  const totalSize = useMemo(
    () => filesList.reduce((acc, { size = 0 }) => acc + size, 0),
    [filesList]
  );

  const isFilesSizeExceeded = useMemo(() => {
    return totalSize > MAX_FILES_SIZE_SENDOUT;
  }, [totalSize]);

  useEffect(() => {
    onDisabledChange?.(isFilesSizeExceeded);
  }, [isFilesSizeExceeded, onDisabledChange]);

  const maxMessageLength = useMemo(() => {
    return socialType === INSTAGRAM
      ? INSTAGRAM_MAX_MESSAGE_LENGTH
      : MAX_FIELD_LENGTH_5000;
  }, [socialType]);

  const messageMode = Form.useWatch(messageModeFormItemName, formInstance);
  const isAiMode = showAiToggle && messageMode === MESSAGE_MODE_AI;

  const activeFormItemName = isAiMode
    ? aiPromptFormItemName
    : messageFieldFormItemName;
  const activePlaceholder = isAiMode
    ? 'Введите инструкцию для ИИ: какое сообщение нужно подготовить клиенту'
    : 'Напишите сообщение для отправки';
  const activeMaxLength = isAiMode ? AI_PROMPT_MAX_LENGTH : maxMessageLength;

  return (
    <div className={`${PREFIX}message-input-with-files-upload`}>
      {showAiToggle && (
        <Form.Item
          name={messageModeFormItemName}
          className="message-mode-form-item"
        >
          <Segmented
            className="message-mode-segmented"
            options={MESSAGE_MODE_OPTIONS}
          />
        </Form.Item>
      )}
      <div className="message-input-container">
        <ExtendedMessage
          key={activeFormItemName}
          placeholder={activePlaceholder}
          className="send-message-text-area"
          withTemplateOffers
          formItemClassName="message-form-item"
          form={formInstance}
          formItemName={activeFormItemName}
          autoSize={{ minRows: 5, maxRows: 5 }}
          socialType={socialType}
          selectedClientIds={selectedClientIds}
          onChange={isAiMode ? undefined : onChange}
          formItemRules={[
            {
              ...(!isAiMode && socialType === LOYALTY_PROGRAM_SENDOUTS
                ? {
                    validator: (_rule: unknown, value: string | undefined) =>
                      Array.from(value ?? '').length <= activeMaxLength
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(`Максимум: ${activeMaxLength} символов`)
                          ),
                  }
                : {
                    max: activeMaxLength,
                    message: `Максимум: ${activeMaxLength} символов`,
                  }),
            },
            ...(isAiMode
              ? [{ required: true, message: 'Введите промпт для ИИ' }]
              : []),
          ]}
        />
        {isAiMode && (
          <Text type="secondary" className="ai-prompt-hint">
            При срабатывании робота ИИ подготовит сообщение по вашей инструкции
            с учётом последних 25 сообщений переписки с клиентом
          </Text>
        )}
      </div>
      {showFiles && (
        <Form.Item
          name="files"
          valuePropName="fileList"
          getValueFromEvent={(e) =>
            e.fileList.filter((file: UploadFile) => {
              const extension = file.name.split('.').pop() ?? '';

              if (extensions.indexOf(extension) < 0) {
                showNotification(
                  `Прикрепите следующие типы файлов: ${extensions.join(', ')}`,
                  FRONT_WARNING
                );

                return false;
              } else {
                return true;
              }
            })
          }
        >
          <Upload
            multiple
            listType="text"
            customRequest={({ onSuccess }) => onSuccess?.(true)}
            accept={extensions.join(', ')}
            onChange={({ fileList }) => {
              setFilesList(fileList);
            }}
          >
            <Button type="primary" icon={<PlusOutlined />}>
              Добавить медиафайлы
            </Button>
          </Upload>
        </Form.Item>
      )}
      {isFilesSizeExceeded && (
        <Text type="danger" className="full-width">
          <div className="error-message">
            Суммарный размер файлов ({(totalSize / MB_RATIO).toFixed(1)} Мб)
            превышает максимальный ({MAX_FILES_SIZE_SENDOUT_MB.toFixed(1)} Мб).
          </div>
        </Text>
      )}
    </div>
  );
};
